import React, { useState, useRef, useEffect } from 'react';
import { FaCameraRetro } from 'react-icons/fa';
import ReactDOM from 'react-dom';

const Photos_Video = ({ files = [], images = [], iconOnly = false }) => {
  // Support both 'images' and 'files' prop for backward compatibility
  const mediaList = Array.isArray(files) && files.length > 0 ? files : images;
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const viewerRef = useRef(null);

  // Filter out any falsy or invalid URLs
  const validMedia = mediaList.filter(url => typeof url === 'string' && url.trim() !== '');

  const openImageViewer = (idx = 0) => {
    setCurrentIndex(idx);
    setImageViewerOpen(true);
  };

  const closeImageViewer = () => {
    setImageViewerOpen(false);
  };

  const prevMedia = (e) => {
    e && e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + validMedia.length) % validMedia.length);
  };

  const nextMedia = (e) => {
    e && e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % validMedia.length);
  };

  const handleImageViewerKeyDown = (e) => {
    if (!imageViewerOpen) return;
    if (e.key === 'ArrowLeft') prevMedia();
    if (e.key === 'ArrowRight') nextMedia();
    if (e.key === 'Escape') closeImageViewer();
  };

  useEffect(() => {
    if (imageViewerOpen) {
      document.addEventListener('keydown', handleImageViewerKeyDown);
      if (viewerRef.current) viewerRef.current.focus();
    } else {
      document.removeEventListener('keydown', handleImageViewerKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleImageViewerKeyDown);
    };
    // eslint-disable-next-line
  }, [imageViewerOpen]);

  if (!validMedia || validMedia.length === 0) return null;

  // Helper to detect file type
  const getFileType = (url) => {
    if (/\.(mp4|webm|ogg)$/i.test(url)) return 'video';
    if (/\.(pdf)$/i.test(url)) return 'pdf';
    if (/\.(jpe?g|png|gif|bmp|svg|webp)$/i.test(url)) return 'image';
    return 'unknown';
  };

  const renderMedia = (url) => {
    const type = getFileType(url);
    if (type === 'image') {
      return (
        <img
          src={url}
          alt={`Property Media ${currentIndex + 1}`}
          className="max-w-full max-h-[80vh] object-contain"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
          }}
        />
      );
    }
    if (type === 'video') {
      return (
        <video
          src={url}
          controls
          autoPlay
          className="max-w-full max-h-[80vh] object-contain bg-black"
        >
          Your browser does not support the video tag.
        </video>
      );
    }
    if (type === 'pdf') {
      return (
        <iframe
          src={url}
          title={`PDF ${currentIndex + 1}`}
          className="max-w-full max-h-[80vh] object-contain bg-white"
        >
          This browser does not support PDFs. Please download the PDF to view it: <a href={url}>Download PDF</a>.
        </iframe>
      );
    }
    return (
      <div className="flex items-center justify-center w-64 h-64 bg-gray-200 text-gray-700">
        File type not supported
      </div>
    );
  };

  return (
    <div>
      <div
        className={
          iconOnly
            ? "p-1.5 rounded-lg bg-slate-100/80 hover:bg-slate-200/50 cursor-pointer flex items-center justify-center"
            : "group flex flex-row items-center gap-2 h-fit bg-[#503691] hover:bg-white border border-blue-800 text-white hover:text-blue-800 px-3 py-1.5 rounded-lg cursor-pointer"
        }
        onClick={() => openImageViewer(0)}
        title="View Photos"
        style={iconOnly ? { width: 32, height: 32 } : {}}
      >
        <FaCameraRetro className={iconOnly ? "w-4 h-4 text-slate-600" : "w-5 h-5 text-white group-hover:text-gray-700"} />
        {!iconOnly && <span className="group-hover:text-blue-800">Photos</span>}
      </div>


      {imageViewerOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center"
          onKeyDown={handleImageViewerKeyDown}
          tabIndex={0}
          ref={viewerRef}
        >
          <div className="relative max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden">
            {/* Close Button */}
            <button
              onClick={closeImageViewer}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-opacity-75"
            >
              ×
            </button>
            {/* Navigation Buttons */}
            {validMedia.length > 1 && (
              <>
                <button
                  onClick={prevMedia}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-opacity-75"
                >
                  ‹
                </button>
                <button
                  onClick={nextMedia}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-opacity-75"
                >
                  ›
                </button>
              </>
            )}
            {/* Media Display */}
            <div className="flex items-center justify-center p-4">
              {renderMedia(validMedia[currentIndex])}
            </div>
            {/* Media Counter */}
            {validMedia.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {currentIndex + 1} / {validMedia.length}
              </div>
            )}
            {/* Keyboard Instructions */}
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-xs">
              Use ← → keys or click buttons to navigate
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Photos_Video;
